/********************************************************************
 * @author:      Kaven
 * @email:       kaven@wuwenkai.com
 * @website:     http://blog.kaven.xyz
 * @file:        [Kaven-Basic] /src/libs/StringPosition.ts
 * @create:      2018-09-01 10:52:52.750
 * @modify:      2025-06-26 10:30:24.607
 * @version:     6.0.0
 * @times:       13
 * @lines:       112
 * @copyright:   Copyright © 2018-2025 Kaven. All Rights Reserved.
 * @description: [description]
 * @license:
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 ********************************************************************/

/**
 * Represents a position or range within a string, defined by a start index and an optional end index.
 * 
 * Provides utility methods and properties to work with string positions, such as calculating length,
 * comparing positions, and associating the position with a specific string.
 * 
 * @remarks
 * - The `Start` property defines the starting index of the position.
 * - The `End` property (optional) defines the exclusive end index of the position.
 * - The `Length` property computes the length of the range as `End - Start`.
 * - The `RelatedString` property can be used to associate this position with a specific string.
 * 
 * @example
 * ```typescript
 * const pos = new StringPosition(5, 10);
 * console.log(pos.Length); // 5
 * ```
 * 
 * @public
 * 
 * @since 4.0.0
 * @version 2022-06-26
 */
export class StringPosition {
    public constructor(start: number);
    public constructor(start: number, end: number);
    public constructor(start: number, end?: number) {

        if (end) {
            if (end < start) {
                this.Start = end;
                this.End = start;
            } else {
                this.Start = start;
                this.End = end;
            }
        } else {
            this.Start = start;
        }
    }

    public static Create(start: number, length: number) {
        const sp = new StringPosition(start);
        sp.Length = length;
        return sp;
    }

    public RelatedString: string | undefined;

    public Start: number;

    public End: number | undefined;

    public get Length() {
        if (this.End) {
            return this.End - this.Start;
        }

        return Number.NaN;
    }

    public set Length(len: number) {
        this.End = this.Start + len;
    }

    public toString() {
        return `${this.Start}-${this.End}`;
    }

    public Equals(another: StringPosition, checkRelatedString = false) {
        if (checkRelatedString) {
            if (this.RelatedString !== another.RelatedString) {
                return false;
            }
        }

        return this.Start === another.Start && this.End === another.End;
    }
}
